import React, {useEffect, useState} from 'react';

const BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

export const UNLINKED_MENTION_BORDER = '2px solid #f1c40f';

const DEFAULT_CONFIG = {unlinkedUnderlineEnabled: true};

let cachedConfig = null;
let inFlightPromise = null;

function applyUnlinkedUnderlineDomFlag(enabled) {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.unlinkedUnderline = enabled ? 'on' : 'off';
}

export async function fetchEntityDisplayConfig({signal} = {}) {
    const response = await fetch(`${BASE}/api/entities/display/config`, {
        method: 'GET',
        headers: {Accept: 'application/json'},
        signal,
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || `Failed to load entity display config (${response.status})`);
    }
    const payload = await response.json();
    return {unlinkedUnderlineEnabled: payload?.unlinkedUnderlineEnabled !== false};
}

function loadEntityDisplayConfig() {
    if (cachedConfig) {
        return Promise.resolve(cachedConfig);
    }
    if (!inFlightPromise) {
        inFlightPromise = fetchEntityDisplayConfig()
            .then((config) => {
                cachedConfig = config;
                applyUnlinkedUnderlineDomFlag(config.unlinkedUnderlineEnabled);
                return config;
            })
            .catch(() => {
                cachedConfig = DEFAULT_CONFIG;
                applyUnlinkedUnderlineDomFlag(true);
                return DEFAULT_CONFIG;
            })
            .finally(() => {
                inFlightPromise = null;
            });
    }
    return inFlightPromise;
}

/**
 * Returns the effective entity display config `{ unlinkedUnderlineEnabled, loading }`.
 * Fetched once per session and shared across the app.
 */
export function useEntityDisplayConfig() {
    const [config, setConfig] = useState(cachedConfig || DEFAULT_CONFIG);
    const [loading, setLoading] = useState(!cachedConfig);

    useEffect(() => {
        if (cachedConfig) {
            setConfig(cachedConfig);
            setLoading(false);
            return undefined;
        }
        let active = true;
        loadEntityDisplayConfig().then((loaded) => {
            if (active) {
                setConfig(loaded);
                setLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, []);

    return {...config, loading};
}

/** Border style for an unlinked entity mention span when the feature flag is enabled. */
export function unlinkedMentionBorderStyle(unlinkedUnderlineEnabled = true) {
    return unlinkedUnderlineEnabled ? UNLINKED_MENTION_BORDER : undefined;
}

/**
 * Whether recognized-but-unlinked mentions should be visible and interactive in the UI.
 * When false, they render and behave as plain text (no underline, hover card, or click).
 */
export function shouldDisplayUnlinkedMentions(unlinkedUnderlineEnabled) {
    if (unlinkedUnderlineEnabled === undefined) {
        return readUnlinkedMentionsDisplayedSync();
    }
    return unlinkedUnderlineEnabled !== false;
}

/** Sync read for ProseMirror plugins and other non-React callers. */
export function readUnlinkedMentionsDisplayedSync() {
    if (typeof document !== 'undefined') {
        const domFlag = document.documentElement.dataset.unlinkedUnderline;
        if (domFlag === 'off') return false;
        if (domFlag === 'on') return true;
    }
    if (cachedConfig) {
        return cachedConfig.unlinkedUnderlineEnabled !== false;
    }
    return DEFAULT_CONFIG.unlinkedUnderlineEnabled !== false;
}

/**
 * Loads entity display config once at app startup and sets `data-unlinked-underline` on `<html>`
 * so CSS can hide yellow underlines app-wide without per-component wiring.
 */
export function EntityDisplayConfigBootstrap() {
    useEntityDisplayConfig();
    return null;
}
